import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  SnackbarContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { errorColor, useAuth } from "../../sdk";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { TextField } from "../../PaceAnalysis/components/Styles";
import { useForm, useWatch } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import ConfirmationDialog from "../../PrimarySetups/components/ConfirmationDialog";

const initialValue = {
  pushRatesTable: [],
};

const formatDate = (date) => {
  const year = String(date.year);
  const month = String(date.month).padStart(2, "0");
  const day = String(date.day).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function PushRatesDialog({
  open,
  handleClose,
  pushRateFromDate,
  setPushRateFromDate,
  pushRateToDate,
  setPushRateToDate,
  pushRatesToChannel,
  recommendedData,
  discount,
  currentHotel,
  sequenceMap,
}) {
  const [step, setStep] = useState(2); // 1 for date selection, 2 for push rate !! skip date selection as per requirement
  const [isChecked, setIsChecked] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(
    false,
  );
  const [
    showDisableOverrideConfirmationPopup,
    setShowDisableOverrideConfirmationPopup,
  ] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const minimumDate = new Date();
  const { hotelId, FILEID } = useParams();
  const { authFetch } = useAuth();

  const {
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    control,
    handleSubmit,
  } = useForm({
    defaultValues: initialValue,
    mode: "all",
  });

  const tableData = useWatch({ name: "pushRatesTable", control });

  const convertDateFormat = (dateStr) => {
    const year = dateStr?.year;
    const month = dateStr?.month?.toString().padStart(2, "0");
    const day = dateStr?.day?.toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const dateformatforTitle = (dateStr) => {
    const year = dateStr?.year;
    const month = dateStr?.month?.toString().padStart(2, "0");
    const day = dateStr?.day?.toString().padStart(2, "0");

    return `${day}-${month}-${year}`;
  };

  const getTable = useCallback(async () => {
    const { get } = await authFetch({
      path: `/hotel/${hotelId}/get-push-price-details?date=${convertDateFormat(
        pushRateFromDate,
      )}&recommendationId=${FILEID}`,
    });
    const { data, error, response } = await get();

    if (response?.ok) {
      const res = data?.[convertDateFormat(pushRateFromDate)];
      const tableData = res?.length
        ? res
            .slice()
            .sort(
              (a, b) =>
                (sequenceMap.get(a.roomRateGroupCode) ?? Infinity) -
                (sequenceMap.get(b.roomRateGroupCode) ?? Infinity),
            )
            .map((item) => {
              return { ...item, isPushOPChange: false };
            })
        : [];
      setValue("pushRatesTable", tableData);
      clearErrors();
    } else {
      console.log(error);
    }
  }, [
    FILEID,
    authFetch,
    clearErrors,
    hotelId,
    pushRateFromDate,
    sequenceMap,
    setValue,
  ]);

  const updatePushRate = useCallback(
    async (value, pushRateFromDate) => {
      setIsLoading(true);
      try {
        const commonFields = {
          date: convertDateFormat(pushRateFromDate),
          recommendationId: Number(FILEID),
        };

        const payload = value?.pushRatesTable?.map((data) => ({
          ...commonFields,
          pushPrice: data?.isPushOverride
            ? Number(data?.pushOverridePrice)
            : Number(data?.pushPrice),
          overrideReason: data?.overrideReason,
          roomId: data?.roomId,
        }));

        const { post } = await authFetch({
          path: `/hotel/${hotelId}/update-push-price-details`,
        });

        const { data, response } = await post(payload);
        if (response.ok) {
          pushRatesToChannel(pushRateFromDate);
          getTable();
        } else {
          setShowSnackbar({
            isError: true,
            message: data?.messageToUser || "Something went wrong",
          });
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    },
    [FILEID, authFetch, getTable, hotelId, pushRatesToChannel],
  );

  useEffect(() => {
    if (pushRateFromDate) {
      setPushRateToDate(pushRateFromDate);
    }
  }, [pushRateFromDate, setPushRateToDate]);

  const handleCheckboxChange = useCallback(
    (event) => {
      if (event.target.checked) {
        const lastDay = new Date(
          pushRateFromDate.year,
          pushRateFromDate.month,
          0,
        ); // Get last day of the month
        setPushRateToDate({ ...pushRateFromDate, day: lastDay.getDate() });
      }
    },
    [pushRateFromDate, setPushRateToDate],
  );

  const getRateComparison = useCallback(
    (value, index, type = "show") => {
      const date = new Date(
        pushRateFromDate.year,
        pushRateFromDate.month - 1,
        pushRateFromDate.day,
      );

      const tableRowData = tableData[index];
      const indexOfDay = date.getDay();

      let minThreshold, maxThreshold;

      if (indexOfDay && currentHotel?.midWeek?.includes(indexOfDay)) {
        minThreshold = tableRowData?.minThresholdWeekday;
        maxThreshold = tableRowData?.MaxThresholdWeekday;
      } else {
        minThreshold = tableRowData?.minThresholdWeekend;
        maxThreshold = tableRowData?.maxThresholdWeekend;
      }

      const numValue = Number(value);

      if (minThreshold && numValue < minThreshold) {
        return type === "show" ? (
          <>
            {Math.round(minThreshold)}
            <RedAsterisk>*</RedAsterisk>
          </>
        ) : (
          `Price should be more than ${Math.round(minThreshold)}`
        );
      }

      if (maxThreshold && numValue > maxThreshold) {
        return type === "show" ? (
          <>
            {maxThreshold}
            <RedAsterisk>*</RedAsterisk>
          </>
        ) : (
          `Price should be less than ${maxThreshold}`
        );
      }

      return type === "show" ? `${value}` : null;
    },
    [
      currentHotel.midWeek,
      pushRateFromDate.day,
      pushRateFromDate.month,
      pushRateFromDate.year,
      tableData,
    ],
  );

  const handleChangeOverrideReason = useCallback(
    (event, index) => {
      const { value } = event.target;
      if (!value) {
        setError(`pushRatesTable.${index}.overrideReason`, {
          type: "custom",
          message: "Please Provide Override Reason",
        });
      } else {
        clearErrors(`pushRatesTable.${index}.overrideReason`);
      }
      setValue(`pushRatesTable.${index}.overrideReason`, value);
    },
    [clearErrors, setError, setValue],
  );

  const handleChangePushRate = useCallback(
    (event, index) => {
      const { value } = event.target;
      const positiveNumberRegex = /^(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)?$/;

      if (positiveNumberRegex.test(value)) {
        const errorMessage = getRateComparison(value, index, "validate");

        if (errorMessage) {
          setError(`pushRatesTable.${index}.pushOverridePrice`, {
            type: "custom",
            message: errorMessage,
          });
        } else {
          clearErrors(`pushRatesTable.${index}.pushOverridePrice`);
        }

        setValue(`pushRatesTable.${index}.pushOverridePrice`, Number(value));
        setValue(`pushRatesTable.${index}.isPushOPChange`, true);
        if (!tableData?.[index]?.overrideReason) {
          setError(`pushRatesTable.${index}.overrideReason`, {
            type: "custom",
            message: "Please Provide Override Reason",
          });
        }
      }
    },
    [clearErrors, getRateComparison, setError, setValue, tableData],
  );

  const disablePushOverride = async (date, roomId, ratePlanId) => {
    try {
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/disable-push-override`,
      });
      const payload = {
        recommendationId: Number(FILEID),
        date: formatDate(date),
        roomId: roomId,
        rateId: ratePlanId,
      };
      const { error, response } = await post(payload);
      if (response?.ok) {
        getTable();
        setShowSnackbar({
          isError: false,
          message: response?.data?.message,
        });
      } else {
        console.log(error);
        setShowSnackbar({
          isError: true,
          message: "Something Went Wrong",
        });
      }
    } catch (error) {
      console.log(error);
      setShowSnackbar({ isError: true, message: "Something Went Wrong" });
    }
  };

  const handleChangeOverrideCheckbox = (event, rowData, index) => {
    if (event.target.checked) {
      setValue(`pushRatesTable.${index}.isPushOverride`, event.target.checked);
      if (!tableData?.[index]?.pushOverridePrice) {
        setError(`pushRatesTable.${index}.pushOverridePrice`, {
          type: "custom",
          message: "Price cannot be Empty or Zero",
        });
      }
    } else {
      setShowDisableOverrideConfirmationPopup({
        date: pushRateFromDate,
        roomId: rowData?.roomId,
        rateId: rowData?.roomRateGroupCode,
      });
    }
  };

  const onSave = useCallback(
    (values) => {
      updatePushRate(values, pushRateFromDate);
    },
    [pushRateFromDate, updatePushRate],
  );
  useEffect(() => {
    getTable();
  }, [getTable]);

  const tableHeadings = [
    { key: "roomTypes", label: "Room Types", style: { minWidth: "150px" } },
    { key: "leftToSell", label: "Left to Sell", style: { minWidth: "50px" } },
    { key: "recommendedRate", label: "Recommended Rate" },
    { key: "overrideRate", label: "Override Rate", style: { width: "160px" } },
    {
      key: "overrideReason",
      label: "Override Reason",
      style: { width: "220px" },
    },
    { key: "promotionalDiscount", label: "Promotional Discount" },
    {
      key: "channelManagerRates",
      label: "Channel Manager Rates",
      colspan: 2,
      style: { minWidth: "200px" },
    },
  ];

  return (
    <StyledChannelDialog
      open={open}
      onClose={handleClose}
      maxWidth={"lg"}
      fullWidth
    >
      <DialogTitle className="content">
        <Typography variant="body1">
          Push Rates to Channel Manager ({dateformatforTitle(pushRateFromDate)})
        </Typography>
      </DialogTitle>

      <DialogContent className="content">
        {step === 1 && (
          <Stack gap="24px" alignItems="center">
            <Stack direction="row" gap="40px">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledCalenderWrapper>
                  <DatePicker
                    inputFormat={"dd/MM/yyyy"}
                    style={{ zIndex: 98 }}
                    value={pushRateFromDate}
                    onChange={(date) => {
                      setPushRateFromDate(date);
                      if (pushRateToDate < date) {
                        setPushRateToDate(date);
                      }
                    }}
                    renderInput={({ inputRef, inputProps, InputProps }) => (
                      <Box
                        className="my-custom-input-class"
                        sx={{
                          display: "flex",
                          position: "relative",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <input
                          ref={inputRef}
                          {...inputProps}
                          readOnly
                          style={{
                            textAlign: "left",
                            marginBottom: "2px",
                            fontSize: "14px",
                            borderRadius: "10px",
                            color: "#000000",
                            outline: "none",
                            height: "30px",
                            border: "none",
                            font: "bold 16px / 20px Roboto",
                            cursor: "pointer",
                            width: "140px",
                            paddingLeft: "14px",
                          }}
                        />
                        <Box
                          className="date-button-container"
                          sx={{ position: "absolute", right: 0, width: "100%" }}
                        >
                          {InputProps?.endAdornment}
                        </Box>
                      </Box>
                    )}
                    minDate={minimumDate}
                  />
                </StyledCalenderWrapper>
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledCalenderWrapper>
                  <DatePicker
                    inputFormat={"dd/MM/yyyy"}
                    style={{ zIndex: 98 }}
                    value={pushRateToDate}
                    onChange={setPushRateToDate}
                    renderInput={({ inputRef, inputProps, InputProps }) => (
                      <Box
                        className="my-custom-input-class"
                        sx={{
                          display: "flex",
                          position: "relative",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <input
                          ref={inputRef}
                          {...inputProps}
                          readOnly
                          style={{
                            textAlign: "left",
                            marginBottom: "2px",
                            fontSize: "14px",
                            borderRadius: "10px",
                            color: "#000000",
                            outline: "none",
                            height: "30px",
                            border: "none",
                            font: "bold 16px / 20px Roboto",
                            cursor: "pointer",
                            width: "140px",
                            paddingLeft: "14px",
                          }}
                        />
                        <Box
                          className="date-button-container"
                          sx={{ position: "absolute", right: 0, width: "100%" }}
                        >
                          {InputProps?.endAdornment}
                        </Box>
                      </Box>
                    )}
                    minDate={pushRateFromDate}
                  />
                </StyledCalenderWrapper>
              </LocalizationProvider>
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: "#163A90",
                    "&.Mui-checked": {
                      color: "#163A90",
                    },
                  }}
                  checked={isChecked}
                  onChange={(e) => {
                    setIsChecked(e.target.checked);
                    handleCheckboxChange(e);
                  }}
                />
              }
              label="Select complete month"
            />

            <Button
              variant="contained"
              onClick={() => {
                setStep(2);
              }}
            >
              Next
            </Button>
          </Stack>
        )}
        {step === 2 && (
          <>
            <Box
              sx={{
                width: "100%",
                padding: "5px",
                maxHeight: "450px",
                overflow: "auto",
                mb: "8px",
              }}
              component="form"
            >
              <Table>
                <TableHead>
                  <StyledTableRow>
                    {tableHeadings.map((column) => (
                      <HeaderTableCell
                        key={column.key}
                        sx={column?.style}
                        colSpan={column?.colspan}
                      >
                        {column.label}
                      </HeaderTableCell>
                    ))}
                  </StyledTableRow>
                </TableHead>

                <TableBody>
                  {tableData.map((rowData, index) => (
                    <React.Fragment key={index}>
                      {/* First row (with common columns + Current Rate) */}
                      <StyledTableRow>
                        <CustomTableCell rowSpan={2}>
                          {rowData?.roomName}
                        </CustomTableCell>
                        <CustomTableCell rowSpan={2}>
                          {rowData?.inventory}
                        </CustomTableCell>
                        <CustomTableCell rowSpan={2}>
                          {getRateComparison(
                            rowData?.recommendedPrice,
                            index,
                            "show",
                          )}
                        </CustomTableCell>
                        <CustomTableCell
                          rowSpan={2}
                          style={{
                            backgroundColor: rowData?.isPushOverride
                              ? "#1976D229"
                              : "#0000000A",
                          }}
                        >
                          <Stack direction={"row"} alignItems={"center"}>
                            <Checkbox
                              size="small"
                              checked={rowData?.isPushOverride}
                              onChange={(event) =>
                                handleChangeOverrideCheckbox(
                                  event,
                                  rowData,
                                  index,
                                )
                              }
                            />
                            <TextField
                              variant="standard"
                              disabled={!rowData?.isPushOverride}
                              value={
                                tableData?.[index]?.pushOverridePrice
                                  ? Math.round(
                                      tableData?.[index]?.pushOverridePrice,
                                    )
                                  : ""
                              }
                              sx={{
                                minWidth: "50px",
                              }}
                              error={
                                !!errors?.pushRatesTable?.[index]
                                  ?.pushOverridePrice
                              }
                              helperText={
                                errors?.pushRatesTable?.[index]
                                  ?.pushOverridePrice
                                  ? errors?.pushRatesTable?.[index]
                                      ?.pushOverridePrice?.message
                                  : ""
                              }
                              inputProps={{
                                style: {
                                  fontSize: "10px",
                                  textAlign: "center",
                                },
                              }}
                              onChange={(e) => handleChangePushRate(e, index)}
                              type="number"
                            />
                          </Stack>
                        </CustomTableCell>
                        <CustomTableCell
                          rowSpan={2}
                          style={{
                            backgroundColor:
                              rowData?.overrideReason || rowData?.isPushOPChange
                                ? "inherit"
                                : "#0000000A",
                          }}
                        >
                          {rowData?.isPushOPChange ? (
                            <TextField
                              variant="standard"
                              value={tableData?.[index]?.overrideReason}
                              sx={{ minWidth: "200px" }}
                              onChange={(e) =>
                                handleChangeOverrideReason(e, index)
                              }
                              error={
                                !!errors?.pushRatesTable?.[index]
                                  ?.overrideReason
                              }
                              helperText={
                                errors?.pushRatesTable?.[index]?.overrideReason
                                  ? errors?.pushRatesTable?.[index]
                                      ?.overrideReason?.message
                                  : ""
                              }
                              inputProps={{
                                style: {
                                  fontSize: "10px",
                                  textAlign: "center",
                                },
                              }}
                            />
                          ) : (
                            <Tooltip title={rowData?.overrideReason}>
                              {rowData?.overrideReason
                                ? `${rowData?.overrideReason?.slice(0, 25)}...`
                                : ""}
                            </Tooltip>
                          )}
                        </CustomTableCell>
                        <CustomTableCell
                          style={{ backgroundColor: "#0000000A" }}
                          rowSpan={2}
                        >
                          {rowData?.promotionalDiscount
                            ? rowData?.promotionalDiscount
                            : "-"}
                        </CustomTableCell>

                        {/* Current Rate Row */}
                        <CustomTableCell
                          style={{ backgroundColor: "#0000000A" }}
                        >
                          Current Rate in CM
                        </CustomTableCell>
                        <CustomTableCell
                          style={{ backgroundColor: "#0000000A" }}
                        >
                          {Math.round(rowData.currentPrice)}
                        </CustomTableCell>
                      </StyledTableRow>

                      {/* Second row (only New Rate) */}
                      <StyledTableRow>
                        <CustomTableCell
                          style={{ backgroundColor: "#1976D229" }}
                        >
                          New Rate to CM
                        </CustomTableCell>
                        <CustomTableCell
                          style={{ backgroundColor: "#1976D229" }}
                        >
                          {Math.round(
                            rowData?.isPushOverride && rowData.pushOverridePrice
                              ? rowData.pushOverridePrice
                              : rowData.pushPrice,
                          )}
                        </CustomTableCell>
                      </StyledTableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <LoadingButton
              loading={isLoading}
              variant="contained"
              type="submit"
              disabled={errors?.pushRatesTable || isLoading}
              onClick={handleSubmit(onSave)}
              sx={{ textTransform: "none" }}
            >
              Submit Rates to CM
            </LoadingButton>
          </>
        )}
      </DialogContent>

      <CloseIcon className="closeButton" onClick={handleClose} />
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        handleCloseConfirmation={() => {
          setIsConfirmationDialogOpen(false);
          handleClose();
        }}
        pushRatesToChannel={() => pushRatesToChannel(pushRateFromDate)}
        tableData={tableData}
      />

      <ConfirmationDialog
        warningIcon
        handleCloseConfirmation={() =>
          setShowDisableOverrideConfirmationPopup(null)
        }
        confirmationText={
          "Are you sure you want to Disable Override Price for this date? Click yes to confirm."
        }
        onSuccess={async () => {
          await disablePushOverride(
            showDisableOverrideConfirmationPopup.date,
            showDisableOverrideConfirmationPopup.roomId,
            showDisableOverrideConfirmationPopup.rateId,
          );
          setShowDisableOverrideConfirmationPopup(null);
        }}
        open={!!showDisableOverrideConfirmationPopup?.roomId}
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={!!showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(null)}
      >
        {showSnackbar && (
          <SnackbarContent
            sx={{
              backgroundColor: showSnackbar.isError ? "#CA3433" : "#228b22",
              color: "#fff",
            }}
            message={showSnackbar.message}
          />
        )}
      </Snackbar>
    </StyledChannelDialog>
  );
}

const StyledChannelDialog = styled(Dialog)(() => ({
  ".MuiPaper-root": {
    overflow: "visible",
  },
  ".closeButton": {
    position: "absolute",
    right: "10px",
    top: "10px",
    cursor: "pointer",
  },
  ".content": {
    padding: "7px 10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "visible",
  },
}));

const StyledCalenderWrapper = styled(Box)((theme) => ({
  borderRadius: "8px",
  boxShadow: "0px 4px 4px rgba(48, 111, 188, 0.2)",
  "&:hover>*": {
    color: "#ffffff",
    ">input": {
      backgroundColor: "#306fbc",
      color: "#ffffff !important",
    },
  },
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#306fbc",
  color: theme.palette.common.white,
  padding: "4px",
  textAlign: "center",
  borderRight: "1px solid #444",
  [`&.${tableCellClasses.head}`]: {
    fontSize: "12px",
  },
}));

const CustomTableCell = styled(TableCell)(({ theme }) => ({
  padding: "6px",
  textAlign: "center",
  borderBottom: "1px solid #eee",
  borderRight: "1px solid #eee",
  borderTop: "1px solid #eee",
  color: "#555",
  [`&.${tableCellClasses.body}`]: {
    fontSize: "10px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  backgroundColor: theme.palette.common.white,
  ...(selected && {
    backgroundColor: "rgba(25, 118, 210, 0.16)",
    border: "2px solid red",
    "& > td": {
      backgroundColor: "rgba(25, 118, 210, 0.16)",
    },
  }),
}));

const RedAsterisk = styled("span")({
  color: "red",
});
